/**
 * Edit Billing Identity Modal
 *
 * Modal for editing an existing billing identity's details.
 */

import { useEffect, useState } from 'react';
import {
  BillingEntityForm,
  type BillingEntityFormData,
  useBillingEntityOperations,
  useBillingEntityOperationsAdmin,
} from '@features/billing-entities';
import { Close as CloseIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';
import type { BillingEntityResponse } from '@store/generated/billing-entities';
import { useSnackbar } from 'notistack';

import { extractApiErrorMessage } from '../utils/errorUtils';

interface EditBillingEntityModalProps {
  open: boolean;
  onClose: () => void;
  entity: BillingEntityResponse;
  onUpdated: () => void;
  /** When provided, uses admin API routes (for admin panel context) */
  organizationId?: string;
}

function entityToFormData(entity: BillingEntityResponse): BillingEntityFormData {
  return {
    billing_npi: entity.billing_npi || '',
    tax_id: '', // Tax ID is masked, user needs to re-enter if changing
    tax_id_type: entity.tax_id_type || 'EIN',
    taxonomy_code: entity.taxonomy_code || '',
    legal_name: entity.legal_name || '',
    doing_business_as: entity.doing_business_as || '',
    billing_address: {
      line1: entity.billing_address?.line1 || '',
      line2: entity.billing_address?.line2 || '',
      city: entity.billing_address?.city || '',
      state: entity.billing_address?.state || '',
      zip: entity.billing_address?.zip || '',
    },
    contact_name: entity.contact_name || '',
    contact_email: entity.contact_email || '',
    contact_phone: entity.contact_phone || '',
  };
}

export function EditBillingEntityModal({
  open,
  onClose,
  entity,
  onUpdated,
  organizationId,
}: EditBillingEntityModalProps) {
  const { enqueueSnackbar } = useSnackbar();
  const providerOps = useBillingEntityOperations();
  const adminOps = useBillingEntityOperationsAdmin();
  const { updateLoading } = organizationId ? adminOps : providerOps;
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [formData, setFormData] = useState<BillingEntityFormData>(() => entityToFormData(entity));

  // Reset form when entity changes
  useEffect(() => {
    setFormData(entityToFormData(entity));
    setSubmitError(null);
  }, [entity]);

  const handleChange = (field: keyof BillingEntityFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddressChange = (field: keyof BillingEntityFormData['billing_address'], value: string) => {
    setFormData(prev => ({
      ...prev,
      billing_address: { ...prev.billing_address, [field]: value },
    }));
  };

  const handleSubmit = async () => {
    setSubmitError(null);

    // Build update request
    const updateRequest: Record<string, unknown> = {
      taxonomy_code: formData.taxonomy_code.trim() || null,
      legal_name: formData.legal_name.trim(),
      doing_business_as: formData.doing_business_as.trim() || null,
      billing_address: {
        line1: formData.billing_address.line1.trim(),
        line2: formData.billing_address.line2.trim() || null,
        city: formData.billing_address.city.trim(),
        state: formData.billing_address.state.trim().toUpperCase(),
        zip: formData.billing_address.zip.trim(),
      },
      contact_name: formData.contact_name.trim() || null,
      contact_email: formData.contact_email.trim(),
      contact_phone: formData.contact_phone.trim(),
    };

    // Only include NPI and tax fields if entity is NOT synced
    if (!entity.stedi_synced) {
      updateRequest.billing_npi = formData.billing_npi.replace(/\D/g, '');

      // Only include tax_id if user entered a new one
      if (formData.tax_id.trim()) {
        updateRequest.tax_id = formData.tax_id.replace(/\D/g, '');
        updateRequest.tax_id_type = formData.tax_id_type;
      }
    }

    try {
      if (organizationId) {
        await adminOps.update({
          organizationId,
          billingEntityId: entity.billing_entity_id,
          updateBillingEntityRequest: updateRequest,
        }).unwrap();
      } else {
        await providerOps.update({
          billingEntityId: entity.billing_entity_id,
          updateBillingEntityRequest: updateRequest,
        }).unwrap();
      }

      enqueueSnackbar('Changes saved. You may need to verify your billing identity again.', {
        variant: 'success',
      });
      onUpdated();
      onClose();
    } catch (err) {
      const errorMessage = extractApiErrorMessage(
        err,
        'Failed to update billing identity. Please check your information and try again.',
      );
      setSubmitError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 10000 });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth={"md"}>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Edit Billing Identity</span>
          <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close" size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {entity.stedi_synced && (
          <Alert severity="info" sx={{ mb: 3 }}>
            This identity has been verified. NPI and Tax ID cannot be changed after verification. To
            use a different NPI or Tax ID, create a new billing identity.
          </Alert>
        )}

        <BillingEntityForm
          data={entity.stedi_synced ? { ...formData, tax_id: entity.tax_id_masked || '' } : formData}
          onChange={handleChange}
          onAddressChange={handleAddressChange}
          disabledFields={
            entity.stedi_synced
              ? { billing_npi: true, tax_id: true, tax_id_type: true }
              : undefined
          }
          taxIdRequired={false}
          taxIdHelperText={
            entity.stedi_synced
              ? undefined
              : 'Only enter if you need to change the Tax ID'
          }
          taxIdPlaceholder={
            entity.stedi_synced
              ? undefined
              : `Current: ${entity.tax_id_masked} (leave blank to keep)`
          }
        />

        {submitError && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {submitError}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={updateLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={updateLoading}
          startIcon={updateLoading ? <CircularProgress size={20} /> : undefined}
        >
          {updateLoading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
